import { FC, useMemo, useState } from 'react';
import { Box, Divider, IconButton, Popover, Tooltip, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { InfoIcon, SquaresFourIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { DomainResultViewModel } from '../../types';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TreemapItem {
  value: number;
  domain: DomainResultViewModel;
}

interface TreemapRect {
  x: number;
  y: number;
  w: number;
  h: number;
  domain: DomainResultViewModel;
}

// ── Squarified treemap (Bruls et al.) ────────────────────────────────────────

function worstAspectRatio(
  rowItems: TreemapItem[],
  rowValue: number,
  rowWidth: number,
  shortSide: number,
): number {
  let worst = 0;
  for (const item of rowItems) {
    const itemH = rowWidth > 0 ? (item.value / rowValue) * shortSide : 0;
    const ratio = rowWidth > 0 && itemH > 0 ? Math.max(rowWidth / itemH, itemH / rowWidth) : Infinity;
    if (ratio > worst) worst = ratio;
  }
  return worst;
}

function squarify(items: TreemapItem[], x: number, y: number, w: number, h: number): TreemapRect[] {
  if (items.length === 0) return [];

  const totalValue = items.reduce((s, i) => s + i.value, 0);
  if (totalValue === 0) return [];

  const rects: TreemapRect[] = [];
  let remaining = [...items];
  let cx = x, cy = y, cw = w, ch = h;

  while (remaining.length > 0) {
    const isHorizontal = cw >= ch;
    const shortSide = isHorizontal ? ch : cw;
    const longSide = isHorizontal ? cw : ch;
    const remTotal = remaining.reduce((s, i) => s + i.value, 0);

    let rowItems: TreemapItem[] = [];
    let rowValue = 0;
    let bestRatio = Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const candidate = remaining[i];
      const newRowValue = rowValue + candidate.value;
      const rowWidth = (newRowValue / remTotal) * longSide;
      const ratio = worstAspectRatio([...rowItems, candidate], newRowValue, rowWidth, shortSide);

      if (ratio < bestRatio || rowItems.length === 0) {
        bestRatio = ratio;
        rowItems = [...rowItems, candidate];
        rowValue += candidate.value;
      } else {
        break;
      }
    }

    const rowWidth = (rowValue / remTotal) * longSide;
    let offset = 0;
    for (const item of rowItems) {
      const itemLen = rowValue > 0 ? (item.value / rowValue) * shortSide : 0;
      rects.push(
        isHorizontal
          ? { x: cx, y: cy + offset, w: rowWidth, h: itemLen, domain: item.domain }
          : { x: cx + offset, y: cy, w: itemLen, h: rowWidth, domain: item.domain },
      );
      offset += itemLen;
    }

    if (isHorizontal) { cx += rowWidth; cw -= rowWidth; }
    else              { cy += rowWidth; ch -= rowWidth; }

    remaining = remaining.slice(rowItems.length);
  }

  return rects;
}

// ── Tile color helper ─────────────────────────────────────────────────────────

function tileIsEvaluated(domain: DomainResultViewModel): boolean {
  return domain.isEvaluated === true && (domain.maturityScore > 0 || domain.gaps > 0);
}

/** A domain with this many (or more) gaps is treated as "Many Gaps" (red). */
const MANY_GAPS_THRESHOLD = 3;

type GapSeverity = 'none' | 'some' | 'many';

/**
 * Gap severity drives the tile color, matching the legend (Compliant / Some
 * Gaps / Many Gaps). Based on the gap count rather than a brittle keyword match
 * on gap titles.
 */
function gapSeverity(domain: DomainResultViewModel): GapSeverity {
  if (domain.gaps <= 0) return 'none';
  return domain.gaps >= MANY_GAPS_THRESHOLD ? 'many' : 'some';
}

// ── Component ─────────────────────────────────────────────────────────────────

interface DomainTreemapChartProps {
  domainResults: DomainResultViewModel[];
  height?: number;
}

const SVG_W = 800;
const TILE_PAD = 1.5;

export const DomainTreemapChart: FC<DomainTreemapChartProps> = observer(({ domainResults, height = 460 }) => {
  const theme = useTheme();
  const { i18n } = useTrans();
  const { isRtl } = useDirection();

  const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);

  const items = useMemo<TreemapItem[]>(
    () =>
      domainResults
        .map((d) => ({ value: Math.max(d.weight ?? 1, 1), domain: d }))
        .sort((a, b) => b.value - a.value),
    [domainResults],
  );

  const rects = useMemo(
    () => squarify(items, 0, 0, SVG_W, height),
    [items, height],
  );

  // Bilingual labels
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const labels = {
    title:      lang === 'ar' ? 'الخريطة الشجرية الموزونة'           : 'Weighted Treemap',
    subtitle:   lang === 'ar' ? 'النطاقات مُرتّبة حسب وزن الامتثال' : 'Domains sized by compliance weight',
    howToRead:  lang === 'ar' ? 'كيفية قراءة هذا الرسم'              : 'How to read this chart',
    manyGaps:   lang === 'ar' ? 'فجوات كثيرة'                        : 'Many Gaps',
    someGaps:   lang === 'ar' ? 'بعض الفجوات'                        : 'Some Gaps',
    compliant:  lang === 'ar' ? 'ملتزم'                              : 'Compliant',
    notEval:    lang === 'ar' ? 'لم يُقيَّم'                         : 'Not Evaluated',
    noData:     lang === 'ar' ? 'لا توجد بيانات للنطاقات'             : 'No domain data',
    notEvalTip: lang === 'ar' ? 'لم يُقيَّم'                         : 'Not evaluated',
    scoreLabel: (score: number, gaps: number) =>
      lang === 'ar'
        ? `${score}%${gaps > 0 ? ` · ${gaps} فجوات` : ''}`
        : `${score}%${gaps > 0 ? ` · ${gaps} gaps` : ''}`,
    tooltip: (score: number, gaps: number, evaluated: boolean) =>
      evaluated
        ? (lang === 'ar' ? `النتيجة: ${score}% · الفجوات: ${gaps}` : `Score: ${score}% · Gaps: ${gaps}`)
        : (lang === 'ar' ? 'لم يُقيَّم' : 'Not evaluated'),
  };

  const infoItems = lang === 'ar'
    ? [
        { label: 'حجم المستطيل',   desc: 'الأكبر = وزن تنظيمي أعلى. تؤثر هذه النطاقات أكثر في نتيجة جاهزيتك.' },
        { label: 'لون المستطيل',   desc: 'أخضر = جاهز (لا فجوات). برتقالي = جزئيًا (توجد فجوات). أحمر = غير جاهز (فجوات حرجة).' },
        { label: 'شريط التقدم',    desc: 'الشريط أسفل كل بلاطة يعرض نسبة الجاهزية (0–100%).' },
        { label: 'ما تبحث عنه',    desc: 'البلاطات الحمراء أو البرتقالية الكبيرة = نطاقات ذات أولوية عالية تستدعي المعالجة أولًا.' },
      ]
    : [
        { label: 'Rectangle size',  desc: 'Larger tiles = higher regulatory weight. More impact on your overall readiness score.' },
        { label: 'Rectangle color', desc: 'Green = fully ready (no gaps). Orange = partially ready. Red = not ready (critical gaps).' },
        { label: 'Score bar',       desc: 'The bar at the bottom of each tile shows readiness progress (0–100%).' },
        { label: 'What to look for', desc: 'Large red or orange tiles signal high-impact domains with low readiness — address these first.' },
      ];

  const legendItems = [
    { key: 'many',      label: labels.manyGaps,  color: theme.palette.error.main },
    { key: 'some',      label: labels.someGaps,  color: theme.palette.warning.main },
    { key: 'compliant', label: labels.compliant,  color: theme.palette.success.main },
    { key: 'na',        label: labels.notEval,    color: theme.palette.grey[600] },
  ];

  if (rects.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
          {labels.noData}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ mt: 0.25, color: 'secondary.main', flexShrink: 0 }}>
            <SquaresFourIcon size={18} />
          </Box>
          <Box>
            <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', display: 'block' }}>
              {labels.title}
            </Typography>
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', display: 'block' }}>
              {labels.subtitle}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => setInfoAnchor(e.currentTarget)}
          sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
        >
          <InfoIcon size={16} />
        </IconButton>
      </Box>

      <Divider sx={{ mx: 2.5, borderColor: 'grey.100' }} />

      {/* ── Info popover ── */}
      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 300,
              p: 2,
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              border: `1px solid ${theme.palette.grey[200]}`,
            },
          },
        }}
      >
        <Typography variant="textSM_semiBold" sx={{ display: 'block', mb: 1 }}>
          {labels.howToRead}
        </Typography>
        {infoItems.map(({ label, desc }) => (
          <Box key={label} sx={{ mb: 1.25 }}>
            <Typography variant="textXS_semiBold" sx={{ color: 'text.primary', display: 'block' }}>{label}</Typography>
            <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>{desc}</Typography>
          </Box>
        ))}
      </Popover>

      {/* ── Body ── */}
      <Box sx={{ px: 1.5, pb: 1.5, pt: 1, overflow: 'hidden' }}>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
          {legendItems.map(({ key, label, color }) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: color, flexShrink: 0 }} />
              <Typography variant="textXS_regular" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* SVG */}
        {/* Pinned LTR: tile x/width maths assume a left-to-right
            coordinate flow. */}
        <NoFlipBox mode="ltr">
          <svg
            viewBox={`0 0 ${SVG_W} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: height,
              display: 'block',
            }}
          >
          {rects.map((r, idx) => {
            const evaluated = tileIsEvaluated(r.domain);
            const severity = gapSeverity(r.domain);

            const color = !evaluated
              ? theme.palette.grey[600]
              : severity === 'none'
              ? theme.palette.success.main
              : severity === 'many'
              ? theme.palette.error.main
              : theme.palette.warning.main;

            const score  = Math.round(r.domain.maturityScore);
            const innerW = Math.max(r.w - TILE_PAD * 2, 0);
            const innerH = Math.max(r.h - TILE_PAD * 2, 0);
            const showLabel = innerW > 55 && innerH > 24;
            const showScore = innerW > 55 && innerH > 44;

            const maxChars = Math.floor(innerW / 7);
            const shortName = r.domain.domainName.length > maxChars
              ? `${r.domain.domainName.slice(0, maxChars - 1)}…`
              : r.domain.domainName;

            const textX = r.x + TILE_PAD + 6;
            const anchor = 'start';
            const barX  = r.x + TILE_PAD;

            return (
              <Tooltip
                key={idx}
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="textXS_semiBold" sx={{ display: 'block' }}>
                      {r.domain.domainName}
                    </Typography>
                    <Typography variant="textXS_regular">
                      {labels.tooltip(score, r.domain.gaps, evaluated)}
                    </Typography>
                  </Box>
                }
                placement="top"
                arrow
              >
                <g style={{ cursor: 'pointer' }}>
                  {/* Tile background */}
                  <rect
                    x={r.x + TILE_PAD} y={r.y + TILE_PAD}
                    width={innerW} height={innerH}
                    rx={4}
                    fill={color} fillOpacity={0.22}
                    stroke={color} strokeOpacity={0.5} strokeWidth={1}
                  />

                  {/* Score progress bar */}
                  {evaluated && innerW > 20 && innerH > 10 && (
                    <rect
                      x={barX}
                      y={r.y + TILE_PAD + innerH - 4}
                      width={Math.max((score / 100) * innerW, 0)}
                      height={4}
                      rx={2}
                      fill={color} fillOpacity={0.7}
                    />
                  )}

                  {/* Domain name */}
                  {showLabel && (
                    <text
                      x={textX} y={r.y + TILE_PAD + 16}
                      fontSize={Math.min(Math.max(innerW / 8, 9), 11)}
                      fontWeight={600}
                      fill={color} opacity={0.9}
                      textAnchor={anchor}
                      style={{ fontFamily: 'inherit', pointerEvents: 'none', unicodeBidi: 'embed' }}
                    >
                      {shortName}
                    </text>
                  )}

                  {/* Score / N/A */}
                  {showScore && (
                    <text
                      x={textX} y={r.y + TILE_PAD + 32}
                      fontSize={10} fontWeight={400}
                      fill={color} opacity={0.7}
                      textAnchor={anchor}
                      style={{ fontFamily: 'inherit', pointerEvents: 'none' }}
                    >
                      {evaluated ? labels.scoreLabel(score, r.domain.gaps) : 'N/A'}
                    </text>
                  )}
                </g>
              </Tooltip>
            );
          })}
          </svg>
        </NoFlipBox>
      </Box>
    </Box>
  );
});
